import * as React from "react";
import { ShieldAlert } from "lucide-react";

import { cn } from "../lib/utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./alert-dialog";

interface SessionTimeoutWarningProps {
  /** Whether the modal is visible */
  open: boolean;
  /** Remaining seconds before session expires (driven by parent) */
  remainingSeconds: number;
  /** Called when the user chooses to stay connected */
  onExtendSession: () => void;
  /** Called when the user chooses to log out */
  onLogout: () => void;
}

function formatSeconds(seconds: number): string {
  const clamped = Math.max(0, seconds);
  const mins = Math.floor(clamped / 60);
  const secs = clamped % 60;
  if (mins > 0) {
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  }
  return `${secs}`;
}

function SessionTimeoutWarning({
  open,
  remainingSeconds,
  onExtendSession,
  onLogout,
}: SessionTimeoutWarningProps) {
  const isCritical = remainingSeconds <= 10;

  return (
    <AlertDialog open={open}>
      <AlertDialogContent
        className="max-w-md border-amber-200/60 bg-gradient-to-b from-amber-50/80 to-white"
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <AlertDialogHeader className="items-center gap-3 sm:items-start">
          <div
            className={cn(
              "flex h-12 w-12 items-center justify-center rounded-full",
              isCritical
                ? "bg-red-100 text-red-600"
                : "bg-amber-100 text-amber-700",
            )}
          >
            <ShieldAlert className="h-6 w-6" aria-hidden="true" />
          </div>

          <AlertDialogTitle className="text-base font-semibold text-stone-900">
            {"Session sur le point d\u2019expirer"}
          </AlertDialogTitle>

          <AlertDialogDescription className="text-sm leading-relaxed text-stone-600">
            {"Pour votre s\u00e9curit\u00e9, votre session expirera dans "}
            <span
              className={cn(
                "inline-flex min-w-[2ch] justify-center tabular-nums font-semibold",
                isCritical
                  ? "animate-pulse text-red-600"
                  : "text-amber-700",
              )}
              aria-live="assertive"
              aria-atomic="true"
              role="timer"
            >
              {formatSeconds(remainingSeconds)}
            </span>
            {" secondes en raison d\u2019inactivit\u00e9."}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter className="mt-2 gap-2 sm:gap-0">
          <AlertDialogCancel
            onClick={onLogout}
            className="border-stone-300 text-stone-700 hover:bg-stone-100 hover:text-stone-900"
          >
            {"Se d\u00e9connecter"}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={onExtendSession}
            className={cn(
              "font-medium",
              isCritical
                ? "bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-500"
                : "bg-amber-600 text-white hover:bg-amber-700 focus-visible:ring-amber-500",
            )}
            autoFocus
          >
            {"Rester connect\u00e9"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

SessionTimeoutWarning.displayName = "SessionTimeoutWarning";

export { SessionTimeoutWarning };
export type { SessionTimeoutWarningProps };
